import { mediaQueryLarge, isMobileBreakpoint } from "@theme/utilities";

// Accordion
class AccordionCustom extends HTMLElement {
  /** @type {HTMLDetailsElement} */
  get details() {
    const details = this.querySelector("details");

    if (!(details instanceof HTMLDetailsElement))
      throw new Error("Details element not found");

    return details;
  }

  /** @type {HTMLElement} */
  get summary() {
    const summary = this.details.querySelector("summary");

    if (!(summary instanceof HTMLElement))
      throw new Error("Summary element not found");

    return summary;
  }

  /** @type {HTMLElement} */
  get #content() {
    const content = this.details.querySelector(".details-content");

    if (!(content instanceof HTMLElement))
      throw new Error("Content element not found");

    return content;
  }

  get #disableOnMobile() {
    return this.dataset.disableOnMobile === "true";
  }

  get #disableOnDesktop() {
    return this.dataset.disableOnDesktop === "true";
  }

  get #closeWithEscape() {
    return this.dataset.closeWithEscape === "true";
  }

  #controller = new AbortController();
  /** @type {ResizeObserver | null} */
  #resizeObserver = null;
  /** @type {ResizeObserver | null} - observes wrapper for customisation accordion so we recalc when layout settles in Chrome */
  #parentResizeObserver = null;

  connectedCallback() {
    const { signal } = this.#controller;

    this.#setDefaultOpenState();
    this.#setupHeightCalculation();

    this.addEventListener("keydown", this.#handleKeyDown, { signal });
    this.summary.addEventListener("click", this.handleClick, { signal });
    mediaQueryLarge.addEventListener("change", this.#handleMediaQueryChange, {
      signal,
    });
  }

  /**
   * Handles the disconnect event.
   */
  disconnectedCallback() {
    // Disconnect all the event listeners
    this.#controller.abort();

    if (this.#resizeObserver instanceof ResizeObserver) {
      this.#resizeObserver.disconnect();
      this.#resizeObserver = null;
    }
    if (this.#parentResizeObserver instanceof ResizeObserver) {
      this.#parentResizeObserver.disconnect();
      this.#parentResizeObserver = null;
    }
  }

  /**
   * Handles the click event.
   * @param {Event} event - The event.
   */
  handleClick = (event) => {
    const isMobile = isMobileBreakpoint();
    const isDesktop = !isMobile;

    // Stop default behaviour from the browser
    if (
      (isMobile && this.#disableOnMobile) ||
      (isDesktop && this.#disableOnDesktop)
    ) {
      event.preventDefault();
      return;
    }
  };

  /**
   * Handles the media query change event.
   */
  #handleMediaQueryChange = () => {
    this.#setDefaultOpenState();
    // Recalculate height after breakpoint change as content may have resized
    requestAnimationFrame(() => {
      if (this.details.open) {
        this.#calculateAndSetHeight();
      }
    });
  };

  /**
   * Sets the default open state of the accordion based on the `open-by-default-on-mobile` and `open-by-default-on-desktop` attributes.
   */
  #setDefaultOpenState() {
    const isMobile = isMobileBreakpoint();

    this.details.open =
      (isMobile && this.hasAttribute("open-by-default-on-mobile")) ||
      (!isMobile && this.hasAttribute("open-by-default-on-desktop"));
  }

  /**
   * Handles keydown events for the accordion
   *
   * @param {KeyboardEvent} event - The keyboard event.
   */
  #handleKeyDown(event) {
    // Close the accordion when used as a menu
    if (event.key === "Escape" && this.#closeWithEscape) {
      event.preventDefault();

      this.details.open = false;
      this.summary.focus();
    }
  }

  /**
   * Sets up height calculation with ResizeObserver and toggle listener
   */
  #setupHeightCalculation() {
    const { signal } = this.#controller;
    const content = this.#content;
    const isCustomisation = this.details.classList.contains(
      "customisation-accordion",
    );

    // Defer initial calculation so Chrome has committed layout (fixes wrong initial height)
    const runInitialHeight = () => {
      this.#calculateAndSetHeight();
    };
    if (isCustomisation) {
      requestAnimationFrame(() => {
        requestAnimationFrame(runInitialHeight);
      });
    } else {
      runInitialHeight();
    }

    // Recalculate height when toggled to open
    this.details.addEventListener(
      "toggle",
      () => {
        if (this.details.open) {
          if (isCustomisation) {
            requestAnimationFrame(() => {
              requestAnimationFrame(() => this.#calculateAndSetHeight());
            });
          } else {
            requestAnimationFrame(() => this.#calculateAndSetHeight());
          }
        }
      },
      { signal },
    );

    // Use ResizeObserver to recalculate when content size changes
    if (window.ResizeObserver) {
      this.#resizeObserver = new ResizeObserver(() => {
        if (this.details.open || isCustomisation) {
          this.#calculateAndSetHeight();
        }
      });

      this.#resizeObserver.observe(content);

      // Customisation: also observe wrapper so we recalc when it gets its real size (Chrome)
      if (isCustomisation) {
        const parent = this.offsetParent || this.parentElement;
        if (parent) {
          this.#parentResizeObserver = new ResizeObserver(() => {
            this.#calculateAndSetHeight();
          });
          this.#parentResizeObserver.observe(parent);
        }
      }
    } else {
      // Fallback to window resize for older browsers
      window.addEventListener(
        "resize",
        () => {
          if (this.details.open) {
            this.#calculateAndSetHeight();
          }
        },
        { signal },
      );
    }
  }

  /**
   * Returns the maximum allowed height for viewport-limited accordions (e.g. customisation accordion).
   * Formula: 100vh - 2 * padding - 38px - header-height. Padding from parent; header-height from --header-height.
   * @returns {number | null} Max height in px, or null if this accordion should not be limited.
   */
  #getViewportMaxHeight() {
    if (!this.details.classList.contains("customisation-accordion")) {
      return null;
    }
    const parent = this.offsetParent || this.parentElement;
    if (!parent) return null;
    // Use parent's computed padding (resolved pixels); --padding-value can be clamp() which parseFloat can't read
    const parentStyle = getComputedStyle(parent);
    const paddingBlockStart = parseFloat(parentStyle.paddingBlockStart) || 0;
    const paddingBlockEnd = parseFloat(parentStyle.paddingBlockEnd) || 0;
    const paddingTotal = paddingBlockStart + paddingBlockEnd;
    // --header-height is set on :root/body by header.js
    const headerHeightRaw = getComputedStyle(document.documentElement)
      .getPropertyValue("--header-height")
      .trim();
    const headerHeight = parseFloat(headerHeightRaw) || 0;
    const viewportHeight = window.innerHeight;
    const max = viewportHeight - paddingTotal - headerHeight;
    console.log("[accordion-custom] viewport max:", {
      paddingBlockStart,
      paddingBlockEnd,
      paddingTotal,
      headerHeightRaw,
      headerHeight,
      viewportHeight,
      max,
    });
    return max;
  }

  /**
   * Calculates the content height and sets it as a CSS variable for smooth transitions.
   * For accordions with a viewport max (e.g. customisation-accordion), uses the smaller of
   * content height and (100vh - 2*padding - 38px - var(--header-height)).
   */
  #calculateAndSetHeight = () => {
    const content = this.#content;
    const details = this.details;
    const isCustomisation = this.details.classList.contains(
      "customisation-accordion",
    );

    // Only customisation accordion has internal content that depends on --accordion-height
    // (product-information .variant-picker). For regular accordions, setting a large value would
    // force the content to that height and we'd measure 9999px. So unconstrain only for customisation.
    if (isCustomisation) {
      const unconstrain = "9999px";
      details.style.setProperty("--accordion-height", unconstrain);
      content.style.setProperty("--accordion-height", unconstrain);
    }

    const currentMaxHeight = content.style.maxHeight;
    const currentOverflow = content.style.overflow;
    content.style.maxHeight = "none";
    content.style.overflow = "visible";

    // Force layout so scrollHeight reflects unconstrained content (Chrome/FF can differ otherwise)
    void content.offsetHeight;
    let height = content.scrollHeight;

    content.style.maxHeight = currentMaxHeight || "";
    content.style.overflow = currentOverflow || "";

    // Cap at viewport max when applicable (e.g. customisation accordion)
    const viewportMax = this.#getViewportMaxHeight();
    const contentHeight = height;
    if (viewportMax != null && viewportMax > 0) {
      height = Math.min(height, viewportMax);
    }
    if (this.details.classList.contains("customisation-accordion")) {
      console.log("[accordion-custom] height set:", {
        contentHeight,
        viewportMax,
        finalHeight: height,
        capped: contentHeight > height,
      });
    }

    details.style.setProperty("--accordion-height", `${height}px`);
    content.style.setProperty("--accordion-height", `${height}px`);

    void details.offsetHeight;
  };
}

if (!customElements.get("accordion-custom")) {
  customElements.define("accordion-custom", AccordionCustom);
}
